import { useContext, useEffect, useState } from "react";
import {
  Box,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  TabContext,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { LkPersonTypeId, SaveTextingQueueRequestDto, TextingQueueDetailDto } from "generated/openApi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { TextingQueueDetailPageHeader } from "./TextingQueueDetailPageHeader";
import { TextingQueueDetailPageTabs } from "./TextingQueueDetailPageTabs";
import { TextingQueueInfo } from "./TextingQueueInfo";
import { TextingQueueItems } from "./TextingQueueItems";

const routeName = "textingQueueDetail";

class Params {
  id: number = 0;
}

class OptionalParams {
  tab?: TextingQueueDetailTab = undefined;
}

export const textingQueueDetailPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Texting Queue Details",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type TextingQueueDetailTab = "Info" | "Recipients";

export const TextingQueueDetailPage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const [currentTab, setCurrentTab] = useState<TextingQueueDetailTab>(optionalParams.tab || "Info");
  const [isEditing, setIsEditing] = useState(false);
  const [textingQueue, setTextingQueue] = useState<TextingQueueDetailDto>({
    id: params.id,
    sent: 0,
    total: 0,
    items: [],
  });
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [textingQueue] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  const fetch = () => {
    ApiService.textingQueue.get(params.id).then((response) => {
      setTextingQueue(response);
      resetUnsavedChanges([response]);
    });
  };

  useEffect(() => {
    fetch();
  }, []);

  const save = () => {
    setValidationErrors({});

    const request: SaveTextingQueueRequestDto = {
      id: textingQueue.id,
      name: textingQueue.name,
      message: textingQueue.message || "",
      scheduledDate: textingQueue.scheduledDate!,
      recipients: (textingQueue.items ?? [])
        .filter((item) => item.personId !== undefined)
        .map((item) => ({
          id: item.personId!,
          phoneNumber: item.destinationPhoneNumber,
          isParent: textingQueue.personTypeId === LkPersonTypeId.Parent,
        })),
    };

    ApiService.textingQueue
      .saveUpdate(request)
      .then(() => {
        setIsEditing(false);
        fetch();
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      })
      .catch((error) => {
        setValidationErrors(error.data);
        dispatch(setAppToast(newToast("error")));
      });
  };

  const deleteQueue = () => {
    ApiService.textingQueue
      .delete(textingQueue.id)
      .then(() => {
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
        navigate("../");
      })
      .catch(() => dispatch(setAppToast(newToast("error"))));
  };

  return (
    <TabContext value={currentTab}>
      <Page
        isForm
        header={
          <TextingQueueDetailPageHeader
            textingQueue={textingQueue}
            isEditing={isEditing}
            setIsEditing={(editing) => {
              setValidationErrors({});
              setIsEditing(editing);
            }}
            unsavedChanges={unsavedChanges}
            save={save}
            deleteQueue={deleteQueue}
          />
        }
        tabs={
          <TextingQueueDetailPageTabs
            itemTotal={textingQueue.items?.length || 0}
            setCurrentTab={setCurrentTab}
          />
        }
      >
        <Box hidden={currentTab !== "Info"}>
          <TextingQueueInfo
            textingQueue={textingQueue}
            setTextingQueue={setTextingQueue}
            isEditing={isEditing}
            validationErrors={validationErrors}
          />
        </Box>
        <Box hidden={currentTab !== "Recipients"}>
          <TextingQueueItems
            textingQueue={textingQueue}
            setTextingQueue={setTextingQueue}
            isEditing={isEditing}
            validationErrors={validationErrors}
          />
        </Box>
      </Page>
    </TabContext>
  );
};
